import React, { useState, useMemo } from "react";
import {
  LayoutDashboard, Users, BadgeCheck, Plane, Building2, Car, FileText, Wallet,
  TrendingUp, BarChart3, UserCog, Settings, Menu, X, Bell, Search, ChevronDown,
  Plus, Pencil, Trash2, LogOut, Eye, Mail, Lock, ArrowUpRight, ArrowDownRight,
  Printer, Download, MapPin, Phone, DollarSign, ChevronLeft, ChevronRight,
  Briefcase, Globe, ArrowRight, Sparkles
} from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell, Legend
} from "recharts";

/* ============================== DESIGN TOKENS ============================== *
 * Primary:  blue-600 (#2563eb)  — trust, corporate travel
 * Accent:   orange-500 (#f97316) — energy, calls-to-action, tickets/boarding
 * Surface:  white / slate-50
 * Text:     slate-800 / slate-500
 * ============================================================================ */
const HEX = { primary: "#2563eb", primaryDark: "#1d4ed8", accent: "#f97316", success: "#16a34a", danger: "#dc2626", slate: "#94a3b8" };

/* ============================== SAMPLE DATA ============================== */
let idCounter = 1000;
const nextId = () => "TA-" + idCounter++;

const revenueTrend = [
  { month: "Jan", revenue: 32000, profit: 9800 },
  { month: "Feb", revenue: 28500, profit: 8100 },
  { month: "Mar", revenue: 41200, profit: 13400 },
  { month: "Apr", revenue: 39800, profit: 11900 },
  { month: "May", revenue: 47600, profit: 15800 },
  { month: "Jun", revenue: 52300, profit: 17200 },
  { month: "Jul", revenue: 49100, profit: 15100 },
  { month: "Aug", revenue: 58900, profit: 19700 },
];

const bookingsByCategory = [
  { category: "Flights", bookings: 142 },
  { category: "Hotels", bookings: 108 },
  { category: "Visa", bookings: 76 },
  { category: "Transport", bookings: 54 },
];

const PIE_COLORS = [HEX.primary, HEX.accent, "#0ea5e9", "#fb923c"];

const recentBookingsData = [
  { id: nextId(), customer: "Ahmed Raza", type: "Flight", date: "2026-07-20", status: "Confirmed", amount: 480 },
  { id: nextId(), customer: "Sana Malik", type: "Hotel", date: "2026-07-19", status: "Pending", amount: 320 },
  { id: nextId(), customer: "Bilal Khan", type: "Visa", date: "2026-07-19", status: "Approved", amount: 150 },
  { id: nextId(), customer: "Fatima Noor", type: "Transport", date: "2026-07-18", status: "Completed", amount: 90 },
  { id: nextId(), customer: "Usman Tariq", type: "Flight", date: "2026-07-17", status: "Cancelled", amount: 610 },
  { id: nextId(), customer: "Ayesha Siddiqui", type: "Hotel", date: "2026-07-16", status: "Confirmed", amount: 275 },
];

const customersData = [
  { id: nextId(), name: "Ahmed Raza", email: "ahmed.raza@mail.com", phone: "+92 300 1234567", nationality: "Pakistani", totalBookings: 8, status: "Active" },
  { id: nextId(), name: "Sana Malik", email: "sana.malik@mail.com", phone: "+92 301 2345678", nationality: "Pakistani", totalBookings: 3, status: "Active" },
  { id: nextId(), name: "John Carter", email: "john.carter@mail.com", phone: "+1 202 555 0148", nationality: "American", totalBookings: 5, status: "Inactive" },
  { id: nextId(), name: "Layla Haddad", email: "layla.haddad@mail.com", phone: "+971 50 123 4567", nationality: "Emirati", totalBookings: 12, status: "Active" },
  { id: nextId(), name: "Bilal Khan", email: "bilal.khan@mail.com", phone: "+92 333 9988776", nationality: "Pakistani", totalBookings: 1, status: "Active" },
];

const visaData = [
  { id: nextId(), applicant: "Ahmed Raza", country: "UAE", visaType: "Tourist", applyDate: "2026-06-01", expiryDate: "2026-12-01", status: "Approved" },
  { id: nextId(), applicant: "Sana Malik", country: "UK", visaType: "Student", applyDate: "2026-07-10", expiryDate: "2027-07-10", status: "Pending" },
  { id: nextId(), applicant: "Bilal Khan", country: "Schengen", visaType: "Business", applyDate: "2026-07-05", expiryDate: "2026-10-05", status: "Approved" },
  { id: nextId(), applicant: "Fatima Noor", country: "USA", visaType: "Tourist", applyDate: "2026-05-22", expiryDate: "2026-11-22", status: "Rejected" },
];

const flightsData = [
  { id: nextId(), passenger: "Ahmed Raza", airline: "Emirates", from: "KHI", to: "DXB", date: "2026-08-02", pnr: "EK4839", amount: 480, status: "Confirmed" },
  { id: nextId(), passenger: "Usman Tariq", airline: "Qatar Airways", from: "LHE", to: "DOH", date: "2026-08-05", pnr: "QR7712", amount: 610, status: "Cancelled" },
  { id: nextId(), passenger: "Layla Haddad", airline: "PIA", from: "ISB", to: "JED", date: "2026-08-10", pnr: "PK9903", amount: 390, status: "Pending" },
];

const hotelsData = [
  { id: nextId(), guest: "Sana Malik", hotelName: "Grand Hyatt", city: "Dubai", checkIn: "2026-08-03", checkOut: "2026-08-07", rooms: 1, amount: 320, status: "Confirmed" },
  { id: nextId(), guest: "Ayesha Siddiqui", hotelName: "Hilton Garden Inn", city: "Istanbul", checkIn: "2026-08-12", checkOut: "2026-08-15", rooms: 2, amount: 275, status: "Confirmed" },
  { id: nextId(), guest: "John Carter", hotelName: "Marriott Downtown", city: "London", checkIn: "2026-09-01", checkOut: "2026-09-04", rooms: 1, amount: 410, status: "Pending" },
];

const transportData = [
  { id: nextId(), client: "Fatima Noor", vehicleType: "Sedan", route: "Airport → Hotel", date: "2026-07-18", driver: "Imran Sheikh", amount: 90, status: "Completed" },
  { id: nextId(), client: "Bilal Khan", vehicleType: "SUV", route: "City Tour", date: "2026-07-25", driver: "Kamran Ali", amount: 140, status: "Scheduled" },
  { id: nextId(), client: "John Carter", vehicleType: "Van", route: "Group Transfer", date: "2026-08-01", driver: "Zubair Ahmed", amount: 210, status: "Scheduled" },
];

const invoiceItemsSample = [
  { desc: "Flight Booking - Emirates EK4839", qty: 1, rate: 480 },
  { desc: "Visa Processing Fee - UAE Tourist", qty: 1, rate: 150 },
  { desc: "Airport Transfer - Sedan", qty: 2, rate: 45 },
];

const invoicesData = [
  { id: nextId(), invoiceNo: "INV-2026-0114", customer: "Ahmed Raza", date: "2026-07-15", dueDate: "2026-07-29", amount: 720, status: "Paid" },
  { id: nextId(), invoiceNo: "INV-2026-0115", customer: "Sana Malik", date: "2026-07-17", dueDate: "2026-07-31", amount: 320, status: "Unpaid" },
  { id: nextId(), invoiceNo: "INV-2026-0116", customer: "Layla Haddad", date: "2026-06-28", dueDate: "2026-07-12", amount: 1240, status: "Overdue" },
  { id: nextId(), invoiceNo: "INV-2026-0117", customer: "Bilal Khan", date: "2026-07-19", dueDate: "2026-08-02", amount: 150, status: "Unpaid" },
];

const staffData = [
  { id: nextId(), name: "Hassan Iqbal", role: "Operations Manager", email: "hassan.iqbal@travelco.com", phone: "+92 301 1112223", department: "Operations", status: "Active" },
  { id: nextId(), name: "Mariam Fayyaz", role: "Sales Executive", email: "mariam.fayyaz@travelco.com", phone: "+92 302 2223334", department: "Sales", status: "Active" },
  { id: nextId(), name: "Danish Iqbal", role: "Accountant", email: "danish.iqbal@travelco.com", phone: "+92 303 3334445", department: "Finance", status: "Active" },
  { id: nextId(), name: "Zoya Ahmed", role: "Customer Support", email: "zoya.ahmed@travelco.com", phone: "+92 304 4445556", department: "Support", status: "Inactive" },
];

const transactionsData = [
  { id: nextId(), type: "Income", category: "Flight Booking", description: "EK4839 - Ahmed Raza", date: "2026-07-20", amount: 480, method: "Bank Transfer" },
  { id: nextId(), type: "Income", category: "Hotel Booking", description: "Grand Hyatt - Sana Malik", date: "2026-07-19", amount: 320, method: "Card" },
  { id: nextId(), type: "Income", category: "Visa Fee", description: "UAE Tourist - Bilal Khan", date: "2026-07-19", amount: 150, method: "Cash" },
  { id: nextId(), type: "Expense", category: "Office Rent", description: "July Rent - Karachi Branch", date: "2026-07-01", amount: 1200, method: "Bank Transfer" },
  { id: nextId(), type: "Expense", category: "Salaries", description: "Staff salaries - July", date: "2026-07-05", amount: 4200, method: "Bank Transfer" },
  { id: nextId(), type: "Expense", category: "Marketing", description: "Social media ads", date: "2026-07-10", amount: 380, method: "Card" },
  { id: nextId(), type: "Income", category: "Transport", description: "Airport transfer - Fatima Noor", date: "2026-07-18", amount: 90, method: "Cash" },
];

/* ============================== SMALL HELPERS ============================== */
function currency(n) {
  return "$" + Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function statusClasses(status) {
  const s = String(status).toLowerCase();
  if (["active", "confirmed", "paid", "approved", "completed"].includes(s)) return "bg-green-50 text-green-700 ring-1 ring-green-200";
  if (["pending", "unpaid", "scheduled"].includes(s)) return "bg-orange-50 text-orange-700 ring-1 ring-orange-200";
  if (["inactive", "cancelled", "rejected", "overdue"].includes(s)) return "bg-red-50 text-red-700 ring-1 ring-red-200";
  return "bg-slate-100 text-slate-600 ring-1 ring-slate-200";
}

function StatusBadge({ status }) {
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusClasses(status)}`}>{status}</span>;
}

const NAV_ITEMS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "customers", label: "Customers", icon: Users },
  { key: "visa", label: "Visa Management", icon: BadgeCheck },
  { key: "flights", label: "Flight Booking", icon: Plane },
  { key: "hotels", label: "Hotel Booking", icon: Building2 },
  { key: "transport", label: "Transportation", icon: Car },
  { key: "invoicing", label: "Invoicing", icon: FileText },
  { key: "accounting", label: "Accounting", icon: Wallet },
  { key: "pnl", label: "Profit & Loss", icon: TrendingUp },
  { key: "reports", label: "Reports", icon: BarChart3 },
  { key: "staff", label: "Staff Management", icon: UserCog },
  { key: "settings", label: "Settings", icon: Settings },
];

/* ============================== LOGIN PAGE ============================== */
function LoginPage({ onLogin }) {
  const [email, setEmail] = useState("admin@travelco.com");
  const [password, setPassword] = useState("");

  return (
    <div className="min-h-screen w-full flex bg-slate-50">
      {/* Branding panel */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-blue-700 via-blue-600 to-blue-500 overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-orange-500/20 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center backdrop-blur">
              <Plane size={22} className="text-orange-400" />
            </div>
            <span className="text-lg font-semibold tracking-tight">TravelCo ERP</span>
          </div>

          <div className="max-w-md">
            <div className="inline-flex items-center gap-1.5 bg-white/10 text-orange-200 text-xs font-medium px-3 py-1 rounded-full mb-5 backdrop-blur">
              <Sparkles size={12} /> Built for travel agencies
            </div>
            <h1 className="text-4xl font-bold leading-tight mb-4">Run your entire agency from one dashboard.</h1>
            <p className="text-blue-100 text-base leading-relaxed">Bookings, visas, invoicing and accounts — synced in real time, wherever your team is working.</p>
          </div>

          <div className="flex items-center gap-6 text-blue-100 text-sm">
            <div><div className="text-2xl font-bold text-white">1,248</div>Customers</div>
            <div className="w-px h-8 bg-white/20" />
            <div><div className="text-2xl font-bold text-white">3,562</div>Bookings</div>
            <div className="w-px h-8 bg-white/20" />
            <div><div className="text-2xl font-bold text-white">98%</div>On-time</div>
          </div>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center">
              <Plane size={18} className="text-white" />
            </div>
            <span className="text-lg font-semibold text-slate-800">TravelCo ERP</span>
          </div>

          <h2 className="text-2xl font-bold text-slate-800 mb-1">Welcome back</h2>
          <p className="text-slate-500 text-sm mb-8">Sign in to manage your agency operations.</p>

          <form onSubmit={(e) => { e.preventDefault(); onLogin(); }} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Work email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" placeholder="you@company.com" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium text-slate-700">Password</label>
                <a href="#" className="text-xs font-medium text-blue-600 hover:text-blue-700">Forgot password?</a>
              </div>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" placeholder="Enter any password" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" defaultChecked className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
              Keep me signed in
            </label>
            <button type="submit" className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2.5 rounded-lg transition shadow-sm shadow-orange-200 flex items-center justify-center gap-2 group">
              Sign in <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
          </form>

          <p className="text-center text-xs text-slate-400 mt-8">This is a demo interface — enter any credentials to continue.</p>
        </div>
      </div>
    </div>
  );
}

/* ============================== SIDEBAR ============================== */
function Sidebar({ active, onSelect, mobileOpen, setMobileOpen, collapsed, setCollapsed }) {
  return (
    <>
      {mobileOpen && <div className="fixed inset-0 bg-slate-900/50 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />}
      <aside className={`fixed lg:sticky top-0 left-0 h-screen z-40 bg-white border-r border-slate-200 flex flex-col transition-all duration-300
        ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0
        ${collapsed ? "lg:w-[76px]" : "lg:w-64"} w-64`}>
        <div className={`flex items-center gap-2.5 h-16 px-4 border-b border-slate-100 ${collapsed ? "lg:justify-center" : ""}`}>
          <div className="w-9 h-9 shrink-0 rounded-xl bg-blue-600 flex items-center justify-center">
            <Plane size={18} className="text-white" />
          </div>
          {!collapsed && <span className="font-semibold text-slate-800 tracking-tight lg:inline hidden">TravelCo ERP</span>}
          <span className="font-semibold text-slate-800 tracking-tight lg:hidden">TravelCo ERP</span>
          <button onClick={() => setMobileOpen(false)} className="ml-auto lg:hidden text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = active === item.key;
            return (
              <button key={item.key} onClick={() => { onSelect(item.key); setMobileOpen(false); }}
                title={collapsed ? item.label : undefined}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors relative
                  ${collapsed ? "lg:justify-center" : ""}
                  ${isActive ? "bg-blue-50 text-blue-700" : "text-slate-500 hover:bg-slate-50 hover:text-slate-800"}`}>
                {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r-full bg-orange-500 lg:block hidden" />}
                <Icon size={18} className="shrink-0" />
                <span className={collapsed ? "lg:hidden" : ""}>{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="p-3 border-t border-slate-100">
          <button onClick={() => setCollapsed(!collapsed)} className="hidden lg:flex w-full items-center justify-center gap-2 px-3 py-2 rounded-lg text-slate-400 hover:bg-slate-50 hover:text-slate-600 text-sm transition">
            {collapsed ? <ChevronRight size={16} /> : <><ChevronLeft size={16} /> Collapse</>}
          </button>
          <button className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 transition ${collapsed ? "lg:justify-center" : ""}`}>
            <LogOut size={18} />
            <span className={collapsed ? "lg:hidden" : ""}>Log out</span>
          </button>
        </div>
      </aside>
    </>
  );
}

/* ============================== HEADER ============================== */
function Header({ title, onMenuClick }) {
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-20 h-16 bg-white/90 backdrop-blur border-b border-slate-200 flex items-center gap-3 px-4 sm:px-6">
      <button onClick={onMenuClick} className="lg:hidden text-slate-500 hover:text-slate-800">
        <Menu size={22} />
      </button>

      <div>
        <h1 className="font-semibold text-slate-800 text-base sm:text-lg leading-none">{title}</h1>
      </div>

      <div className="hidden md:flex items-center flex-1 max-w-sm ml-6">
        <div className="relative w-full">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input placeholder="Search anything…" className="w-full pl-9 pr-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" />
        </div>
      </div>

      <div className="ml-auto flex items-center gap-2 sm:gap-4">
        <div className="relative">
          <button onClick={() => { setNotifOpen(!notifOpen); setProfileOpen(false); }} className="relative p-2 rounded-lg text-slate-500 hover:bg-slate-50 hover:text-slate-800 transition">
            <Bell size={19} />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-orange-500 ring-2 ring-white" />
          </button>
          {notifOpen && (
            <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-lg ring-1 ring-slate-200 py-2 z-30">
              <div className="px-4 py-2 text-sm font-semibold text-slate-700 border-b border-slate-100">Notifications</div>
              {[
                { t: "New invoice INV-2026-0117 created", s: "2 min ago" },
                { t: "Visa application approved — Bilal Khan", s: "1 hr ago" },
                { t: "Flight PK9903 payment pending", s: "3 hrs ago" },
              ].map((n, i) => (
                <div key={i} className="px-4 py-2.5 hover:bg-slate-50 transition cursor-pointer">
                  <p className="text-sm text-slate-700 leading-snug">{n.t}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{n.s}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="w-px h-6 bg-slate-200 hidden sm:block" />

        <div className="relative">
          <button onClick={() => { setProfileOpen(!profileOpen); setNotifOpen(false); }} className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-slate-50 transition">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-blue-400 flex items-center justify-center text-white text-xs font-semibold">HI</div>
            <div className="hidden sm:block text-left">
              <p className="text-sm font-medium text-slate-700 leading-none">Hassan Iqbal</p>
              <p className="text-xs text-slate-400 mt-0.5">Operations Manager</p>
            </div>
            <ChevronDown size={14} className="text-slate-400 hidden sm:block" />
          </button>
          {profileOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg ring-1 ring-slate-200 py-1.5 z-30">
              <button className="w-full text-left px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">My Profile</button>
              <button className="w-full text-left px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">Account Settings</button>
              <div className="my-1 border-t border-slate-100" />
              <button className="w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-50">Log out</button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

/* ============================== SHARED UI: Card / Modal / Table bits ============================== */
function Card({ children, className = "" }) {
  return <div className={`bg-white rounded-2xl border border-slate-200 shadow-sm ${className}`}>{children}</div>;
}

function StatCard({ label, value, icon: Icon, trend, trendUp, tint }) {
  return (
    <Card className="p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-500">{label}</p>
          <p className="text-2xl font-bold text-slate-800 mt-1.5">{value}</p>
        </div>
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${tint}`}>
          <Icon size={20} />
        </div>
      </div>
      <div className={`flex items-center gap-1 mt-3 text-xs font-medium ${trendUp ? "text-green-600" : "text-red-500"}`}>
        {trendUp ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
        {trend} <span className="text-slate-400 font-normal">vs last month</span>
      </div>
    </Card>
  );
}

function Modal({ open, onClose, title, children, wide }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
      <div className={`relative bg-white rounded-2xl shadow-xl w-full ${wide ? "max-w-2xl" : "max-w-md"} max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white rounded-t-2xl">
          <h3 className="font-semibold text-slate-800">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1 rounded-lg hover:bg-slate-50 transition">
            <X size={18} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function FieldInput({ field, value, onChange }) {
  const common = "w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition";
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">{field.label}</label>
      {field.type === "select" ? (
        <select className={common} value={value || ""} onChange={(e) => onChange(field.key, e.target.value)}>
          <option value="" disabled>Select {field.label.toLowerCase()}</option>
          {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input type={field.type || "text"} className={common} value={value || ""} placeholder={field.placeholder || ""}
          onChange={(e) => onChange(field.key, e.target.value)} />
      )}
    </div>
  );
}

/* ============================== GENERIC CRUD MODULE ============================== */
function CrudModule({ title, subtitle, icon: Icon, columns, fields, initialData, addLabel, renderRowExtra }) {
  const [data, setData] = useState(initialData);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});

  const hasStatus = columns.some((c) => c.key === "status");
  const statuses = useMemo(() => ["All", ...Array.from(new Set(data.map((d) => d.status)))], [data]);

  const filtered = data.filter((row) => {
    const matchesSearch = Object.values(row).join(" ").toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "All" || row.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const openAdd = () => { setFormData({}); setEditingId(null); setShowForm(true); };
  const openEdit = (row) => { setFormData(row); setEditingId(row.id); setShowForm(true); };
  const remove = (id) => { if (window.confirm("Delete this record?")) setData(data.filter((d) => d.id !== id)); };

  const submit = (e) => {
    e.preventDefault();
    if (editingId) {
      setData(data.map((d) => (d.id === editingId ? { ...d, ...formData } : d)));
    } else {
      setData([{ id: nextId(), ...formData }, ...data]);
    }
    setShowForm(false);
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center"><Icon size={20} /></div>
          <div>
            <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
            <p className="text-sm text-slate-500">{subtitle}</p>
          </div>
        </div>
        <button onClick={openAdd} className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition shadow-sm shadow-orange-200 shrink-0">
          <Plus size={16} /> {addLabel}
        </button>
      </div>

      <Card className="p-4 mb-5">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${title.toLowerCase()}…`}
              className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" />
          </div>
          {hasStatus && (
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2.5 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:w-48">
              {statuses.map((s) => <option key={s} value={s}>{s === "All" ? "All statuses" : s}</option>)}
            </select>
          )}
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                {columns.map((c) => <th key={c.key} className="text-left font-semibold px-5 py-3 whitespace-nowrap">{c.label}</th>)}
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.id} className="border-t border-slate-100 hover:bg-slate-50/70 transition-colors">
                  {columns.map((c) => (
                    <td key={c.key} className="px-5 py-3.5 whitespace-nowrap text-slate-700">
                      {c.key === "status" ? <StatusBadge status={row.status} /> : c.render ? c.render(row) : row[c.key]}
                    </td>
                  ))}
                  <td className="px-5 py-3.5 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-1.5">
                      {renderRowExtra && renderRowExtra(row)}
                      <button onClick={() => openEdit(row)} className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition"><Pencil size={15} /></button>
                      <button onClick={() => remove(row.id)} className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={columns.length + 1} className="px-5 py-10 text-center text-slate-400 text-sm">No records match your search.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 text-xs text-slate-400">{filtered.length} of {data.length} records</div>
      </Card>

      <Modal open={showForm} onClose={() => setShowForm(false)} title={editingId ? `Edit ${title.slice(0, -1) || title}` : `Add new ${title.slice(0, -1) || title}`}>
        <form onSubmit={submit} className="space-y-4">
          {fields.map((f) => <FieldInput key={f.key} field={f} value={formData[f.key]} onChange={(k, v) => setFormData({ ...formData, [k]: v })} />)}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setShowForm(false)} className="flex-1 px-4 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition">Cancel</button>
            <button type="submit" className="flex-1 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition">{editingId ? "Save changes" : "Add record"}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

/* ============================== DASHBOARD ============================== */
function Dashboard() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
        <StatCard label="Total Customers" value="1,248" icon={Users} trend="+12.5%" trendUp tint="bg-blue-50 text-blue-600" />
        <StatCard label="Total Bookings" value="3,562" icon={Plane} trend="+8.2%" trendUp tint="bg-orange-50 text-orange-500" />
        <StatCard label="Revenue" value={currency(482300)} icon={DollarSign} trend="+15.3%" trendUp tint="bg-blue-50 text-blue-600" />
        <StatCard label="Profit" value={currency(128450)} icon={TrendingUp} trend="+9.7%" trendUp tint="bg-orange-50 text-orange-500" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <Card className="p-5 xl:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-slate-800">Revenue & Profit</h3>
              <p className="text-xs text-slate-400">Last 8 months</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={revenueTrend}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={HEX.primary} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={HEX.primary} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="prof" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={HEX.accent} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={HEX.accent} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v / 1000}k`} />
              <Tooltip formatter={(v) => currency(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }} />
              <Area type="monotone" dataKey="revenue" stroke={HEX.primary} strokeWidth={2.5} fill="url(#rev)" name="Revenue" />
              <Area type="monotone" dataKey="profit" stroke={HEX.accent} strokeWidth={2.5} fill="url(#prof)" name="Profit" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <h3 className="font-semibold text-slate-800 mb-1">Bookings by Category</h3>
          <p className="text-xs text-slate-400 mb-4">This month</p>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={bookingsByCategory} dataKey="bookings" nameKey="category" innerRadius={55} outerRadius={80} paddingAngle={3}>
                {bookingsByCategory.map((entry, i) => <Cell key={entry.category} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {bookingsByCategory.map((b, i) => (
              <div key={b.category} className="flex items-center gap-2 text-xs text-slate-600">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                {b.category} <span className="text-slate-400 ml-auto">{b.bookings}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-800">Recent Bookings</h3>
          <button className="text-sm font-medium text-blue-600 hover:text-blue-700">View all</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-slate-400 text-xs uppercase tracking-wide">
                <th className="text-left font-semibold py-2.5">Customer</th>
                <th className="text-left font-semibold py-2.5">Type</th>
                <th className="text-left font-semibold py-2.5">Date</th>
                <th className="text-left font-semibold py-2.5">Status</th>
                <th className="text-right font-semibold py-2.5">Amount</th>
              </tr>
            </thead>
            <tbody>
              {recentBookingsData.map((b) => (
                <tr key={b.id} className="border-t border-slate-100">
                  <td className="py-3 font-medium text-slate-700">{b.customer}</td>
                  <td className="py-3 text-slate-500">{b.type}</td>
                  <td className="py-3 text-slate-500">{b.date}</td>
                  <td className="py-3"><StatusBadge status={b.status} /></td>
                  <td className="py-3 text-right font-medium text-slate-700">{currency(b.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

/* ============================== INVOICE PREVIEW ============================== */
function InvoicePreview({ invoice, onClose }) {
  if (!invoice) return null;
  const items = invoiceItemsSample;
  const subtotal = items.reduce((s, i) => s + i.qty * i.rate, 0);
  const tax = Math.round(subtotal * 0.05);
  const total = subtotal + tax;

  return (
    <Modal open={!!invoice} onClose={onClose} title="Invoice Preview" wide>
      <div className="border border-slate-200 rounded-xl p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 pb-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-blue-600 flex items-center justify-center"><Plane size={20} className="text-white" /></div>
            <div>
              <p className="font-bold text-slate-800 leading-none">TravelCo Agency</p>
              <p className="text-xs text-slate-400 mt-1">123 Shahrah-e-Faisal, Karachi, Pakistan</p>
              <p className="text-xs text-slate-400">billing@travelco.com · +92 21 111 222 333</p>
            </div>
          </div>
          <div className="text-left sm:text-right">
            <p className="text-lg font-bold text-blue-700">{invoice.invoiceNo}</p>
            <StatusBadge status={invoice.status} />
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 py-6 text-sm">
          <div>
            <p className="text-xs text-slate-400 uppercase mb-1">Billed to</p>
            <p className="font-medium text-slate-700">{invoice.customer}</p>
          </div>
          <div>
            <p className="text-xs text-slate-400 uppercase mb-1">Invoice date</p>
            <p className="font-medium text-slate-700">{invoice.date}</p>
          </div>
          <div>
            <p className="text-xs text-slate-400 uppercase mb-1">Due date</p>
            <p className="font-medium text-slate-700">{invoice.dueDate}</p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                <th className="text-left px-4 py-2.5 rounded-l-lg">Description</th>
                <th className="text-center px-4 py-2.5">Qty</th>
                <th className="text-right px-4 py-2.5">Rate</th>
                <th className="text-right px-4 py-2.5 rounded-r-lg">Amount</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="px-4 py-3 text-slate-700">{it.desc}</td>
                  <td className="px-4 py-3 text-center text-slate-500">{it.qty}</td>
                  <td className="px-4 py-3 text-right text-slate-500">{currency(it.rate)}</td>
                  <td className="px-4 py-3 text-right font-medium text-slate-700">{currency(it.qty * it.rate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end mt-4">
          <div className="w-full sm:w-64 space-y-2 text-sm">
            <div className="flex justify-between text-slate-500"><span>Subtotal</span><span>{currency(subtotal)}</span></div>
            <div className="flex justify-between text-slate-500"><span>Tax (5%)</span><span>{currency(tax)}</span></div>
            <div className="flex justify-between font-bold text-slate-800 text-base pt-2 border-t border-slate-100"><span>Total due</span><span>{currency(total)}</span></div>
          </div>
        </div>

        <p className="text-xs text-slate-400 mt-6 pt-4 border-t border-slate-100">Thank you for booking with TravelCo Agency. Payment due within 14 days of invoice date.</p>
      </div>

      <div className="flex gap-3 mt-6">
        <button onClick={() => window.print()} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition"><Printer size={16} /> Print</button>
        <button className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition"><Download size={16} /> Download PDF</button>
      </div>
    </Modal>
  );
}

function InvoicingModule() {
  const [previewInvoice, setPreviewInvoice] = useState(null);
  return (
    <>
      <CrudModule
        title="Invoicing"
        subtitle="Create and track customer invoices"
        icon={FileText}
        addLabel="New Invoice"
        initialData={invoicesData}
        columns={[
          { key: "invoiceNo", label: "Invoice #" },
          { key: "customer", label: "Customer" },
          { key: "date", label: "Date" },
          { key: "dueDate", label: "Due Date" },
          { key: "amount", label: "Amount", render: (r) => currency(r.amount) },
          { key: "status", label: "Status" },
        ]}
        fields={[
          { key: "invoiceNo", label: "Invoice number", placeholder: "INV-2026-0118" },
          { key: "customer", label: "Customer name" },
          { key: "date", label: "Invoice date", type: "date" },
          { key: "dueDate", label: "Due date", type: "date" },
          { key: "amount", label: "Amount", type: "number" },
          { key: "status", label: "Status", type: "select", options: ["Paid", "Unpaid", "Overdue"] },
        ]}
        renderRowExtra={(row) => (
          <button onClick={(e) => { e.stopPropagation(); setPreviewInvoice(row); }} className="p-1.5 rounded-lg text-slate-400 hover:text-orange-600 hover:bg-orange-50 transition"><Eye size={15} /></button>
        )}
      />
      <InvoicePreview invoice={previewInvoice} onClose={() => setPreviewInvoice(null)} />
    </>
  );
}

/* ============================== ACCOUNTING ============================== */
function AccountingModule() {
  const [tab, setTab] = useState("Income");
  const [data, setData] = useState(transactionsData);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({});

  const filtered = data.filter((t) => t.type === tab);

  const submit = (e) => {
    e.preventDefault();
    setData([{ id: nextId(), type: tab, ...formData, amount: Number(formData.amount) }, ...data]);
    setShowForm(false);
    setFormData({});
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center"><Wallet size={20} /></div>
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Accounting</h2>
            <p className="text-sm text-slate-500">Track income and expenses</p>
          </div>
        </div>
        <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition shadow-sm shadow-orange-200 shrink-0">
          <Plus size={16} /> Add Transaction
        </button>
      </div>

      <div className="inline-flex bg-slate-100 rounded-lg p-1 mb-5">
        {["Income", "Expense"].map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 rounded-md text-sm font-medium transition ${tab === t ? "bg-white shadow-sm text-slate-800" : "text-slate-500"}`}>{t}</button>
        ))}
      </div>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                <th className="text-left font-semibold px-5 py-3">Date</th>
                <th className="text-left font-semibold px-5 py-3">Category</th>
                <th className="text-left font-semibold px-5 py-3">Description</th>
                <th className="text-left font-semibold px-5 py-3">Method</th>
                <th className="text-right font-semibold px-5 py-3">Amount</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50/70">
                  <td className="px-5 py-3.5 text-slate-500">{t.date}</td>
                  <td className="px-5 py-3.5 font-medium text-slate-700">{t.category}</td>
                  <td className="px-5 py-3.5 text-slate-500">{t.description}</td>
                  <td className="px-5 py-3.5 text-slate-500">{t.method}</td>
                  <td className={`px-5 py-3.5 text-right font-semibold ${tab === "Income" ? "text-green-600" : "text-red-500"}`}>{tab === "Income" ? "+" : "-"}{currency(t.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Modal open={showForm} onClose={() => setShowForm(false)} title={`Add ${tab}`}>
        <form onSubmit={submit} className="space-y-4">
          <FieldInput field={{ key: "category", label: "Category" }} value={formData.category} onChange={(k, v) => setFormData({ ...formData, [k]: v })} />
          <FieldInput field={{ key: "description", label: "Description" }} value={formData.description} onChange={(k, v) => setFormData({ ...formData, [k]: v })} />
          <FieldInput field={{ key: "date", label: "Date", type: "date" }} value={formData.date} onChange={(k, v) => setFormData({ ...formData, [k]: v })} />
          <FieldInput field={{ key: "amount", label: "Amount", type: "number" }} value={formData.amount} onChange={(k, v) => setFormData({ ...formData, [k]: v })} />
          <FieldInput field={{ key: "method", label: "Payment method", type: "select", options: ["Cash", "Card", "Bank Transfer"] }} value={formData.method} onChange={(k, v) => setFormData({ ...formData, [k]: v })} />
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setShowForm(false)} className="flex-1 px-4 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition">Cancel</button>
            <button type="submit" className="flex-1 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition">Add {tab.toLowerCase()}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

/* ============================== PROFIT & LOSS ============================== */
function ProfitLossModule() {
  const income = transactionsData.filter((t) => t.type === "Income").reduce((s, t) => s + t.amount, 0);
  const expense = transactionsData.filter((t) => t.type === "Expense").reduce((s, t) => s + t.amount, 0);
  const net = income - expense;
  const margin = ((net / income) * 100).toFixed(1);

  const byCategory = {};
  transactionsData.forEach((t) => {
    const key = t.category;
    byCategory[key] = byCategory[key] || { category: key, income: 0, expense: 0 };
    byCategory[key][t.type.toLowerCase()] += t.amount;
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <StatCard label="Total Income" value={currency(income)} icon={ArrowUpRight} trend="+11.4%" trendUp tint="bg-green-50 text-green-600" />
        <StatCard label="Total Expenses" value={currency(expense)} icon={ArrowDownRight} trend="+4.1%" trendUp={false} tint="bg-red-50 text-red-500" />
        <StatCard label="Net Profit" value={currency(net)} icon={TrendingUp} trend={`${margin}% margin`} trendUp tint="bg-blue-50 text-blue-600" />
      </div>

      <Card className="p-5">
        <h3 className="font-semibold text-slate-800 mb-4">Monthly Income vs Expense</h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={revenueTrend.map((r) => ({ month: r.month, income: r.revenue, expense: Math.round(r.revenue - r.profit) }))}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v / 1000}k`} />
            <Tooltip formatter={(v) => currency(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }} />
            <Legend />
            <Bar dataKey="income" fill={HEX.primary} radius={[6, 6, 0, 0]} name="Income" />
            <Bar dataKey="expense" fill={HEX.accent} radius={[6, 6, 0, 0]} name="Expense" />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card className="overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100"><h3 className="font-semibold text-slate-800">Breakdown by Category</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-xs uppercase">
                <th className="text-left font-semibold px-5 py-3">Category</th>
                <th className="text-right font-semibold px-5 py-3">Income</th>
                <th className="text-right font-semibold px-5 py-3">Expense</th>
                <th className="text-right font-semibold px-5 py-3">Net</th>
              </tr>
            </thead>
            <tbody>
              {Object.values(byCategory).map((c) => (
                <tr key={c.category} className="border-t border-slate-100">
                  <td className="px-5 py-3 font-medium text-slate-700">{c.category}</td>
                  <td className="px-5 py-3 text-right text-green-600">{c.income ? currency(c.income) : "—"}</td>
                  <td className="px-5 py-3 text-right text-red-500">{c.expense ? currency(c.expense) : "—"}</td>
                  <td className="px-5 py-3 text-right font-semibold text-slate-700">{currency(c.income - c.expense)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

/* ============================== REPORTS ============================== */
function ReportsModule() {
  const reports = [
    { name: "Sales Report", desc: "Monthly revenue by booking type and agent.", icon: BarChart3, tint: "bg-blue-50 text-blue-600" },
    { name: "Booking Summary", desc: "Flights, hotels, visa and transport volumes.", icon: Plane, tint: "bg-orange-50 text-orange-500" },
    { name: "Customer Report", desc: "New vs returning customers, retention trends.", icon: Users, tint: "bg-blue-50 text-blue-600" },
    { name: "Financial Report", desc: "Income statement, expenses and profit margin.", icon: Wallet, tint: "bg-orange-50 text-orange-500" },
    { name: "Staff Performance", desc: "Bookings closed and revenue per staff member.", icon: UserCog, tint: "bg-blue-50 text-blue-600" },
    { name: "Visa Status Report", desc: "Applications by country, type and outcome.", icon: BadgeCheck, tint: "bg-orange-50 text-orange-500" },
  ];
  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center"><BarChart3 size={20} /></div>
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Reports</h2>
          <p className="text-sm text-slate-500">Generate and export operational reports</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {reports.map((r) => {
          const Icon = r.icon;
          return (
            <Card key={r.name} className="p-5 hover:shadow-md hover:-translate-y-0.5 transition-all">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${r.tint}`}><Icon size={20} /></div>
              <h3 className="font-semibold text-slate-800 mb-1">{r.name}</h3>
              <p className="text-sm text-slate-500 mb-4">{r.desc}</p>
              <button className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700">
                <Download size={15} /> Export CSV
              </button>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

/* ============================== SETTINGS ============================== */
function SettingsModule() {
  const [tab, setTab] = useState("Company");
  const tabs = ["Company", "Users & Roles", "Notifications", "Billing"];
  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center"><Settings size={20} /></div>
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Settings</h2>
          <p className="text-sm text-slate-500">Manage your agency preferences</p>
        </div>
      </div>

      <div className="flex gap-1 border-b border-slate-200 mb-6 overflow-x-auto">
        {tabs.map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition ${tab === t ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}>{t}</button>
        ))}
      </div>

      {tab === "Company" && (
        <Card className="p-6 max-w-2xl">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-2xl bg-blue-600 flex items-center justify-center"><Plane size={26} className="text-white" /></div>
            <div>
              <button className="text-sm font-medium text-blue-600 hover:text-blue-700">Change logo</button>
              <p className="text-xs text-slate-400">PNG, JPG up to 2MB</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <FieldInput field={{ key: "n", label: "Agency name" }} value="TravelCo Agency" onChange={() => {}} />
            <FieldInput field={{ key: "e", label: "Support email", type: "email" }} value="support@travelco.com" onChange={() => {}} />
            <FieldInput field={{ key: "p", label: "Phone" }} value="+92 21 111 222 333" onChange={() => {}} />
            <FieldInput field={{ key: "c", label: "Currency", type: "select", options: ["USD", "PKR", "AED", "GBP"] }} value="USD" onChange={() => {}} />
            <div className="sm:col-span-2"><FieldInput field={{ key: "a", label: "Office address" }} value="123 Shahrah-e-Faisal, Karachi, Pakistan" onChange={() => {}} /></div>
          </div>
          <button className="mt-6 px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition">Save changes</button>
        </Card>
      )}

      {tab === "Users & Roles" && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-slate-500 text-xs uppercase"><th className="text-left px-5 py-3">Name</th><th className="text-left px-5 py-3">Role</th><th className="text-left px-5 py-3">Access</th></tr></thead>
              <tbody>
                {staffData.map((s) => (
                  <tr key={s.id} className="border-t border-slate-100">
                    <td className="px-5 py-3 font-medium text-slate-700">{s.name}</td>
                    <td className="px-5 py-3 text-slate-500">{s.role}</td>
                    <td className="px-5 py-3"><span className="px-2.5 py-1 rounded-full text-xs bg-blue-50 text-blue-700">{s.department === "Management" ? "Admin" : "Standard"}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {tab === "Notifications" && (
        <Card className="p-6 max-w-2xl space-y-4">
          {["Email me new booking alerts", "Email me invoice due reminders", "SMS alerts for visa status changes", "Weekly performance summary"].map((n) => (
            <label key={n} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
              <span className="text-sm text-slate-700">{n}</span>
              <input type="checkbox" defaultChecked className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 w-4 h-4" />
            </label>
          ))}
        </Card>
      )}

      {tab === "Billing" && (
        <Card className="p-6 max-w-2xl">
          <div className="flex items-center justify-between p-4 rounded-xl bg-blue-50 mb-4">
            <div>
              <p className="font-semibold text-slate-800">Growth Plan</p>
              <p className="text-sm text-slate-500">$79/month · renews Aug 22, 2026</p>
            </div>
            <button className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition">Upgrade</button>
          </div>
          <p className="text-sm text-slate-500">Manage payment methods and view past invoices from this panel.</p>
        </Card>
      )}
    </div>
  );
}

/* ============================== MODULE CONFIGS (reuse CrudModule) ============================== */
function CustomersModule() {
  return <CrudModule title="Customers" subtitle="Manage your customer database" icon={Users} addLabel="Add Customer" initialData={customersData}
    columns={[
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "nationality", label: "Nationality" },
      { key: "totalBookings", label: "Bookings" },
      { key: "status", label: "Status" },
    ]}
    fields={[
      { key: "name", label: "Full name" },
      { key: "email", label: "Email", type: "email" },
      { key: "phone", label: "Phone", type: "tel" },
      { key: "nationality", label: "Nationality" },
      { key: "totalBookings", label: "Total bookings", type: "number" },
      { key: "status", label: "Status", type: "select", options: ["Active", "Inactive"] },
    ]} />;
}

function VisaModule() {
  return <CrudModule title="Visa Management" subtitle="Track visa applications and statuses" icon={BadgeCheck} addLabel="New Application" initialData={visaData}
    columns={[
      { key: "applicant", label: "Applicant" },
      { key: "country", label: "Country" },
      { key: "visaType", label: "Visa Type" },
      { key: "applyDate", label: "Applied" },
      { key: "expiryDate", label: "Expiry" },
      { key: "status", label: "Status" },
    ]}
    fields={[
      { key: "applicant", label: "Applicant name" },
      { key: "country", label: "Destination country" },
      { key: "visaType", label: "Visa type", type: "select", options: ["Tourist", "Business", "Student", "Work"] },
      { key: "applyDate", label: "Application date", type: "date" },
      { key: "expiryDate", label: "Expiry date", type: "date" },
      { key: "status", label: "Status", type: "select", options: ["Pending", "Approved", "Rejected"] },
    ]} />;
}

function FlightsModule() {
  return <CrudModule title="Flight Booking" subtitle="Manage flight reservations" icon={Plane} addLabel="New Booking" initialData={flightsData}
    columns={[
      { key: "passenger", label: "Passenger" },
      { key: "airline", label: "Airline" },
      { key: "route", label: "Route", render: (r) => `${r.from} → ${r.to}` },
      { key: "date", label: "Date" },
      { key: "pnr", label: "PNR" },
      { key: "amount", label: "Amount", render: (r) => currency(r.amount) },
      { key: "status", label: "Status" },
    ]}
    fields={[
      { key: "passenger", label: "Passenger name" },
      { key: "airline", label: "Airline" },
      { key: "from", label: "From (airport code)" },
      { key: "to", label: "To (airport code)" },
      { key: "date", label: "Departure date", type: "date" },
      { key: "pnr", label: "PNR" },
      { key: "amount", label: "Amount", type: "number" },
      { key: "status", label: "Status", type: "select", options: ["Confirmed", "Pending", "Cancelled"] },
    ]} />;
}

function HotelsModule() {
  return <CrudModule title="Hotel Booking" subtitle="Manage hotel reservations" icon={Building2} addLabel="New Booking" initialData={hotelsData}
    columns={[
      { key: "guest", label: "Guest" },
      { key: "hotelName", label: "Hotel" },
      { key: "city", label: "City" },
      { key: "checkIn", label: "Check-in" },
      { key: "checkOut", label: "Check-out" },
      { key: "rooms", label: "Rooms" },
      { key: "amount", label: "Amount", render: (r) => currency(r.amount) },
      { key: "status", label: "Status" },
    ]}
    fields={[
      { key: "guest", label: "Guest name" },
      { key: "hotelName", label: "Hotel name" },
      { key: "city", label: "City" },
      { key: "checkIn", label: "Check-in date", type: "date" },
      { key: "checkOut", label: "Check-out date", type: "date" },
      { key: "rooms", label: "Rooms", type: "number" },
      { key: "amount", label: "Amount", type: "number" },
      { key: "status", label: "Status", type: "select", options: ["Confirmed", "Pending", "Cancelled"] },
    ]} />;
}

function TransportModule() {
  return <CrudModule title="Transportation" subtitle="Manage transfers and vehicle bookings" icon={Car} addLabel="New Booking" initialData={transportData}
    columns={[
      { key: "client", label: "Client" },
      { key: "vehicleType", label: "Vehicle" },
      { key: "route", label: "Route" },
      { key: "date", label: "Date" },
      { key: "driver", label: "Driver" },
      { key: "amount", label: "Amount", render: (r) => currency(r.amount) },
      { key: "status", label: "Status" },
    ]}
    fields={[
      { key: "client", label: "Client name" },
      { key: "vehicleType", label: "Vehicle type", type: "select", options: ["Sedan", "SUV", "Van", "Bus"] },
      { key: "route", label: "Route / destination" },
      { key: "date", label: "Date", type: "date" },
      { key: "driver", label: "Driver name" },
      { key: "amount", label: "Amount", type: "number" },
      { key: "status", label: "Status", type: "select", options: ["Scheduled", "Completed", "Cancelled"] },
    ]} />;
}

function StaffModule() {
  return <CrudModule title="Staff Management" subtitle="Manage your team and access" icon={UserCog} addLabel="Add Staff" initialData={staffData}
    columns={[
      { key: "name", label: "Name" },
      { key: "role", label: "Role" },
      { key: "department", label: "Department" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "status", label: "Status" },
    ]}
    fields={[
      { key: "name", label: "Full name" },
      { key: "role", label: "Job title" },
      { key: "department", label: "Department", type: "select", options: ["Sales", "Operations", "Finance", "Support", "Management"] },
      { key: "email", label: "Email", type: "email" },
      { key: "phone", label: "Phone", type: "tel" },
      { key: "status", label: "Status", type: "select", options: ["Active", "Inactive"] },
    ]} />;
}

/* ============================== APP SHELL ============================== */
export default function App() {
  const [authed, setAuthed] = useState(false);
  const [active, setActive] = useState("dashboard");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  if (!authed) return <LoginPage onLogin={() => setAuthed(true)} />;

  const titleMap = Object.fromEntries(NAV_ITEMS.map((n) => [n.key, n.label]));

  const renderModule = () => {
    switch (active) {
      case "dashboard": return <Dashboard />;
      case "customers": return <CustomersModule />;
      case "visa": return <VisaModule />;
      case "flights": return <FlightsModule />;
      case "hotels": return <HotelsModule />;
      case "transport": return <TransportModule />;
      case "invoicing": return <InvoicingModule />;
      case "accounting": return <AccountingModule />;
      case "pnl": return <ProfitLossModule />;
      case "reports": return <ReportsModule />;
      case "staff": return <StaffModule />;
      case "settings": return <SettingsModule />;
      default: return <Dashboard />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex text-slate-800" style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      <Sidebar active={active} onSelect={setActive} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} collapsed={collapsed} setCollapsed={setCollapsed} />
      <div className="flex-1 min-w-0 flex flex-col">
        <Header title={titleMap[active]} onMenuClick={() => setMobileOpen(true)} />
        <main className="flex-1 p-4 sm:p-6 max-w-[1600px] w-full mx-auto">
          {renderModule()}
        </main>
        <footer className="px-6 py-4 text-center text-xs text-slate-400">TravelCo ERP · Demo interface with sample data</footer>
      </div>
    </div>
  );
}
